import { useEffect, useId, useRef } from "react";
import type { IResendErrorProps } from "@b2bportal/core-self-serve/overridable";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import {
  Button,
  ButtonTypeEnum,
  DialogClose,
  InfoBanner,
  InfoBannerSeverity,
} from "@tenant/ui";
import { ContactSupport } from "../ContactSupport";
import styles from "./ResendError.module.scss";

export const ResendError = ({ copy, onTryAgain }: IResendErrorProps) => {
  const [block, cn] = useModuleBEM(styles, "ResendError");
  const dialogCloseRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const subtitleId = useId();

  // Move focus to the error title on mount so AT announces it. Submitting
  // unmounts the form's focused button, orphaning focus, and Radix's focus scope
  // redirects that to the dialog close button. Running on the next frame lands
  // after Radix, so the heading keeps focus. aria-describedby pulls in the
  // subtitle so both lines are read.
  useEffect(() => {
    const raf = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      <DialogClose ref={dialogCloseRef} style={{ display: "none" }} />
      <div className={block} data-error>
        <div className={cn("header")}>
          <InfoBanner
            severity={InfoBannerSeverity.ERROR}
            icon={IconNameEnum.warning}
          >
            <span
              ref={titleRef}
              tabIndex={-1}
              aria-describedby={subtitleId}
              className={cn("banner-text")}
            >
              {copy.error.title}
            </span>
          </InfoBanner>
          <p id={subtitleId} className={cn("subtitle")}>
            {copy.error.subtitle}
          </p>
        </div>

        <div className={cn("support")}>
          <ContactSupport onStartChat={() => dialogCloseRef.current?.click()} />
        </div>

        <div className={cn("footer")}>
          <Button
            buttonType={ButtonTypeEnum.Primary}
            className={cn("try-again-btn")}
            onClick={onTryAgain}
          >
            {copy.error.tryAgainBtn}
          </Button>
        </div>
      </div>
    </>
  );
};
